import { Dayjs } from 'dayjs'
import {
  EmploymentProfile,
  hasStatutoryPremiums
} from '@/features/employee-management/types/employment.model'
import { Shift } from '../types/schedule.model'
import {
  SHIFT_TYPES,
  ShiftType,
  isAllDayType,
  isPaidType,
  shiftTypeDefinition
} from '../types/shiftType.model'
import {
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
  dayjs,
  shiftDurationHours
} from './schedule'

/*
  Kodeks pracy art. 151(7) does not fix the night hours, it fixes the bracket
  they sit in: the employer designates EIGHT hours somewhere between 21:00 and
  07:00. So the length is law and the start is a venue's own decision, which is
  why the start is configurable and the end is derived from it.

  Counting the whole 21:00-07:00 bracket, as this used to, overstates the night
  hours of every venue that designated a narrower window - ten hours charged
  where the law asks for eight.
*/
export const NIGHT_WINDOW_MINUTES = 8 * MINUTES_IN_HOUR

/** The bracket the designated window has to start inside. */
export const NIGHT_EARLIEST_START_MINUTES = 21 * MINUTES_IN_HOUR
export const NIGHT_LATEST_START_MINUTES = 23 * MINUTES_IN_HOUR

/** What a venue gets before it designates anything of its own. */
export const DEFAULT_NIGHT_START_MINUTES = NIGHT_EARLIEST_START_MINUTES

/**
 * The window's end, always eight hours on from its start and therefore always
 * past midnight (21:00 -> 05:00 … 23:00 -> 07:00).
 */
export const nightEndMinutes = (startMinutes: number): number =>
  (startMinutes + NIGHT_WINDOW_MINUTES) % MINUTES_IN_DAY

/**
 * Minutes of a shift falling inside the designated night window.
 *
 * The window wraps midnight, so within one day it is two ranges, 00:00-end and
 * start-24:00, and an overnight shift stores its end past 1440, so the
 * following day's pair is measured as well. That is what makes 22:00-04:00 six
 * night hours rather than the two the first day alone would give.
 */
export const nightMinutes = (
  shift: Shift,
  startMinutes: number = DEFAULT_NIGHT_START_MINUTES
): number => {
  const endMinutes = nightEndMinutes(startMinutes)
  const overlap = (from: number, to: number) =>
    Math.max(
      0,
      Math.min(shift.endMinutes, to) - Math.max(shift.startMinutes, from)
    )

  return (
    overlap(0, endMinutes) +
    overlap(startMinutes, MINUTES_IN_DAY) +
    overlap(MINUTES_IN_DAY, MINUTES_IN_DAY + endMinutes) +
    overlap(MINUTES_IN_DAY + startMinutes, 2 * MINUTES_IN_DAY)
  )
}

/**
 * Sunday and public-holiday work is the premium case in Polish labour law -
 * Saturday is NOT, despite the common assumption: a Saturday inside someone's
 * roster is ordinary time. Only Sundays are detectable from the date alone;
 * public holidays need a calendar the app does not have yet, so they are
 * counted as ordinary days and the UI says so.
 *
 * A shift counts whole against the day it STARTS on, which is also the day the
 * roster files it under. That is deliberate, and closer to Kodeks pracy art.
 * 151(9) §2 than splitting at midnight would be: statutory Sunday work runs
 * 06:00 Sunday to 06:00 Monday, so a Saturday-night shift ending Sunday
 * morning is Saturday's (zero Sunday hours here, which is right) and a
 * Sunday-night shift ending Monday morning is Sunday's throughout (its full
 * length here, also right). It diverges only for shifts that begin before
 * 06:00, one starting Sunday 00:00 is counted whole where the statute gives
 * it to Saturday, one starting Monday 00:00 is counted as nothing where the
 * statute still calls it Sunday work. Both are rare on a restaurant roster,
 * and the alternative is a second window to maintain for no premium that is
 * currently paid: these hours are reported, not costed.
 */
export const isSunday = (date: string): boolean => dayjs(date).day() === 0

export interface PayrollRow {
  userId: string
  name: string
  profile?: EmploymentProfile
  /** Hours already behind us this month, counting a running shift pro rata. */
  hoursToDate: number
  /** Every hour rostered this month, past and future. */
  hoursScheduled: number
  /** Pay earned so far, in the venue's minor units. Undefined without a rate. */
  payToDateMinorUnits?: number
  /** Pay if the whole month is worked as rostered, in minor units. */
  payEstimatedMinorUnits?: number
  /** Rostered above the contracted weekly hours × weeks in the month. */
  exceedsContractedHours: boolean
  /** Hours above the contracted monthly norm, 0 when no norm is set. */
  overtimeHours: number
  /** Hours inside the statutory 21:00-07:00 night window. */
  nightHours: number
  /** Hours worked on Sundays. Public holidays are not detected. */
  sundayHours: number
  /**
   * Every entry type totalled separately.
   *
   * Paid types are counted in hours, absences in days: an all-day entry is
   * stored as 00:00-24:00, so totalling it in hours would add 24 to a month and
   * read as a fortnight of overtime. Days are also the unit anyone checking a
   * holiday allowance is actually counting in.
   */
  byType: ShiftTypeTotals
}

/** Hours for paid types, whole days for absences. Keyed by every known type. */
export type ShiftTypeTotals = Record<ShiftType, { hours: number; days: number }>

const emptyTotals = (): ShiftTypeTotals =>
  SHIFT_TYPES.reduce((acc, definition) => {
    acc[definition.type] = { hours: 0, days: 0 }
    return acc
  }, {} as ShiftTypeTotals)

const minutesSinceMidnight = (moment: Dayjs): number =>
  moment.hour() * MINUTES_IN_HOUR + moment.minute()

/**
 * Hours of a single shift that have actually elapsed at `now`.
 *
 * A shift in progress counts pro rata rather than all-or-nothing: "current pay"
 * read halfway through an eight-hour shift should not jump by a full shift's
 * worth the moment it ends.
 *
 * `now` is placed on the shift's own clock, minutes since midnight of the day
 * it started, because an overnight end is stored past 1440 and minutes since
 * *today's* midnight never are. Comparing on today's clock paid a 16:00-02:00
 * shift out in full at midnight, four hours after it was earned.
 */
export const elapsedHours = (shift: Shift, now: Dayjs): number => {
  const daysSinceStart = now.startOf('day').diff(dayjs(shift.date), 'day')
  const elapsedMinutes =
    daysSinceStart * MINUTES_IN_DAY + minutesSinceMidnight(now)

  if (elapsedMinutes <= shift.startMinutes) return 0
  if (elapsedMinutes >= shift.endMinutes) return shiftDurationHours(shift)

  return (elapsedMinutes - shift.startMinutes) / MINUTES_IN_HOUR
}

/** Inclusive at both ends, the way a person reads "od … do …". */
export interface DateRange {
  from: Dayjs
  to: Dayjs
}

export const monthRange = (month: Dayjs): DateRange => ({
  from: month.startOf('month'),
  to: month.endOf('month')
})

export const shiftsInRange = (shifts: Shift[], range: DateRange): Shift[] =>
  shifts.filter(shift => {
    const day = dayjs(shift.date)
    return !day.isBefore(range.from, 'day') && !day.isAfter(range.to, 'day')
  })

/**
 * Pay for a month, per employee.
 *
 * Deliberately base pay only, hours × rate. Night and overtime premiums are
 * statutory for an employment contract, but they key off the national minimum
 * wage, which changes yearly and is not something to hardcode blind. Contract
 * type is carried through so the caller can say which rows are understated.
 */
export const buildPayrollRows = (
  employees: Array<{ userId: string; name: string }>,
  shifts: Shift[],
  profilesByUserId: Record<string, EmploymentProfile>,
  range: DateRange,
  now: Dayjs = dayjs(),
  nightStartMinutes: number = DEFAULT_NIGHT_START_MINUTES
): PayrollRow[] => {
  const rangeShifts = shiftsInRange(shifts, range)
  // The contracted norm is weekly, so it scales with however long the range is.
  // Counted in whole calendar days, the same granularity shiftsInRange filters
  // on: the endpoints carry whatever time of day the picker left on them, and
  // a raw diff of 1 Aug 23:59 to 15 Aug 00:00 is 13 days for 15 days of
  // shifts, and an understated norm turns ordinary hours into overtime.
  const daysInRange =
    Math.max(
      0,
      range.to.startOf('day').diff(range.from.startOf('day'), 'day')
    ) + 1
  const weeksInRange = daysInRange / 7

  return employees.map(employee => {
    const all = rangeShifts.filter(shift => shift.userId === employee.userId)

    /*
      Hours, overtime, night and Sunday premiums are about time on the clock, so
      only paid types feed them. An absence left in this sum would bill a
      fortnight of holiday as worked, which is exactly backwards.
    */
    const own = all.filter(shift => isPaidType(shift.type))

    const byType = all.reduce((acc, shift) => {
      const { type } = shiftTypeDefinition(shift.type)
      if (isAllDayType(shift.type)) {
        acc[type].days += 1
      } else {
        acc[type].hours += shiftDurationHours(shift)
      }
      return acc
    }, emptyTotals())

    const profile = profilesByUserId[employee.userId]
    const rate = profile?.hourlyRateMinorUnits

    const hoursToDate = own.reduce(
      (sum, shift) => sum + elapsedHours(shift, now),
      0
    )
    const hoursScheduled = own.reduce(
      (sum, shift) => sum + shiftDurationHours(shift),
      0
    )

    const rangeNorm = profile?.weeklyHoursLimit
      ? profile.weeklyHoursLimit * weeksInRange
      : undefined

    return {
      userId: employee.userId,
      name: employee.name,
      profile,
      byType,
      hoursToDate,
      hoursScheduled,
      overtimeHours: rangeNorm ? Math.max(0, hoursScheduled - rangeNorm) : 0,
      nightHours: own.reduce(
        (sum, shift) =>
          sum + nightMinutes(shift, nightStartMinutes) / MINUTES_IN_HOUR,
        0
      ),
      sundayHours: own.reduce(
        (sum, shift) =>
          sum + (isSunday(shift.date) ? shiftDurationHours(shift) : 0),
        0
      ),
      payToDateMinorUnits: rate ? Math.round(hoursToDate * rate) : undefined,
      payEstimatedMinorUnits: rate
        ? Math.round(hoursScheduled * rate)
        : undefined,
      exceedsContractedHours: rangeNorm ? hoursScheduled > rangeNorm : false
    }
  })
}

export const sumPay = (
  rows: PayrollRow[],
  key: 'payToDateMinorUnits' | 'payEstimatedMinorUnits'
): number => rows.reduce((sum, row) => sum + (row[key] ?? 0), 0)

/** Rows whose pay cannot be computed, so the totals can say what they omit. */
export const rowsMissingRate = (rows: PayrollRow[]): PayrollRow[] =>
  rows.filter(row => row.payEstimatedMinorUnits === undefined)

/** Rows understated because statutory premiums are not modelled yet. */
export const rowsWithUnmodelledPremiums = (rows: PayrollRow[]): PayrollRow[] =>
  rows.filter(row => hasStatutoryPremiums(row.profile?.contractType))
